<template>
  <vs-popup
    id="chat-flow-confirm-popup"
    title="Aplicar Fluxo em Massa"
    :active.sync="isSidebarActiveLocal"
    class="chat-flow-confirm-popup"
    @close="closeSidebar"
  >
    <div class="wizard-content">
      <div class="stepper">
        <div class="step-chip" :class="stepClass(1)">1. Resumo</div>
        <div class="step-chip" :class="stepClass(2)">2. Risco</div>
        <div class="step-chip" :class="stepClass(3)">3. Assinatura</div>
      </div>

      <div v-if="currentStep === 1" class="step-panel">
        <p class="step-description">
          Revise os dados que serão copiados do tenant de origem e confirme a lista exata de destinos.
        </p>

        <div class="summary-grid">
          <div class="summary-card">
            <small class="summary-label">Fluxo</small>
            <p>{{ flowTypeLabel }}</p>
          </div>

          <div class="summary-card">
            <small class="summary-label">Dash de origem</small>
            <p>{{ formatTenant(sourceTenant) }}</p>
          </div>

          <div class="summary-card">
            <small class="summary-label">Itens do fluxo</small>
            <p>{{ sourceOptionsCount }}</p>
          </div>

          <div class="summary-card">
            <small class="summary-label">Configurações</small>
            <p>{{ scheduleLabel }}</p>
          </div>
        </div>

        <div class="tenant-list-card">
          <div class="tenant-list-header">
            <strong>Dashs/Tenants de destino</strong>
            <span>{{ selectedTargetsCount }} selecionada(s)</span>
          </div>

          <ul class="tenant-list">
            <li v-for="tenant in selectedTargets" :key="`${tenant.id || 'no-id'}-${tenant.name}`">
              <span>{{ tenant.name || '-' }}</span>
              <small>{{ tenantMetaLabel(tenant) }}</small>
            </li>
          </ul>
        </div>
      </div>

      <div v-if="currentStep === 2" class="step-panel">
        <p class="step-description">
          Esta etapa exige ciência explícita dos impactos antes de liberar a execução.
        </p>

        <div class="risk-box">
          <p>Esta ação sobrescreve integralmente o fluxo atual das dashs selecionadas. Os destinos perderão a configuração anterior assim que a replicação for concluída.</p>
          <p><strong>A ação é irrecuperável.</strong></p>
        </div>

        <div v-if="hasBackgroundProcessing" class="background-info">
          <strong>Processamento em segundo plano</strong>
          <p>
            As 3 primeiras lojas serão processadas durante esta requisição. As demais
            {{ backgroundCount }} entrarão em uma fila e serão executadas em segundo plano —
            o progresso aparece no histórico abaixo e atualiza sozinho.
          </p>
        </div>
      </div>

      <div v-if="currentStep === 3" class="step-panel">
        <p class="step-description">
          Para autorizar a operação, o usuário ativo deve assinar digitalmente com a própria senha.
        </p>

        <div class="signature-box">
          <small class="summary-label">Usuário responsável</small>
          <p>{{ activeUserLabel }}</p>
        </div>

        <vs-input
          type="password"
          class="w-full"
          label="Senha"
          placeholder="Digite sua senha para autorizar"
          v-model="authorizationPassword"
          @keyup.enter="confirmReplication"
        />
      </div>

      <div class="wizard-actions">
        <vs-button type="border" color="dark" @click="closeSidebar" :disabled="isLoading">
          Cancelar
        </vs-button>

        <vs-button
          v-if="currentStep > 1"
          type="border"
          @click="prevStep"
          :disabled="isLoading"
        >
          Voltar
        </vs-button>

        <vs-button
          v-if="currentStep < 3"
          color="primary"
          @click="nextStep"
          :disabled="isLoading || !canAdvance"
        >
          Próximo
        </vs-button>

        <vs-button
          v-if="currentStep === 3"
          color="danger"
          :disabled="!canConfirm || isLoading"
          @click="confirmReplication"
        >
          {{ isLoading ? 'Aplicando...' : 'Aplicar em Massa' }}
        </vs-button>
      </div>
    </div>
  </vs-popup>
</template>

<script>
export default {
  name: 'chat-flow-confirm-popup',
  props: {
    isSidebarActive: {
      type: Boolean,
      required: true
    },
    sourceTenant: {
      type: Object,
      default() {
        return {};
      }
    },
    flowType: {
      type: String,
      default: 'passive'
    },
    sourceOptionsCount: {
      type: Number,
      default: 0
    },
    includeSchedule: {
      type: Boolean,
      default: true
    },
    selectedTargets: {
      type: Array,
      default() {
        return [];
      }
    },
    activeUser: {
      type: Object,
      default() {
        return {};
      }
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isSidebarActiveLocal: false,
      currentStep: 1,
      authorizationPassword: ''
    };
  },
  computed: {
    activeUserLabel() {
      if (this.activeUser && this.activeUser.name && this.activeUser.email) {
        return `${this.activeUser.name} (${this.activeUser.email})`;
      }

      return this.activeUser && (this.activeUser.name || this.activeUser.email)
        ? (this.activeUser.name || this.activeUser.email)
        : '-';
    },
    canAdvance() {
      return this.selectedTargetsCount > 0 && !!(this.sourceTenant && this.sourceTenant.name);
    },
    canConfirm() {
      return this.authorizationPassword.trim() !== '' && this.selectedTargetsCount > 0;
    },
    flowTypeLabel() {
      return this.flowType === 'active' ? 'Bot de Campanhas (Ativo)' : 'Bot de Triagem (Passivo)';
    },
    scheduleLabel() {
      if (this.flowType !== 'passive') { return 'Não se aplica'; }
      return this.includeSchedule ? 'Fluxo + configurações do bot' : 'Somente fluxo';
    },
    selectedTargetsCount() {
      return this.selectedTargets.length;
    },
    backgroundCount() {
      return Math.max(this.selectedTargetsCount - 3, 0);
    },
    hasBackgroundProcessing() {
      return this.backgroundCount > 0;
    }
  },
  watch: {
    isSidebarActive: {
      immediate: true,
      handler(value) {
        this.isSidebarActiveLocal = value;
        if (value) {
          this.resetState();
        }
      }
    }
  },
  methods: {
    stepClass(step) {
      return {
        'step-chip-active': this.currentStep === step,
        'step-chip-done': this.currentStep > step
      };
    },
    formatTenant(tenant) {
      if (!tenant || (!tenant.name && !tenant.id)) {
        return '-';
      }

      const idLabel = tenant.id ? `#${tenant.id}` : 'sem ID';
      return `${tenant.name || '-'} (${idLabel})`;
    },
    tenantMetaLabel(tenant) {
      const labels = [];

      if (tenant && tenant.id) {
        labels.push(`ID ${tenant.id}`);
      }

      if (tenant && tenant.cnpj) {
        labels.push(`CNPJ ${tenant.cnpj}`);
      }

      return labels.length > 0 ? labels.join(' | ') : 'Sem metadados adicionais';
    },
    resetState() {
      this.currentStep = 1;
      this.authorizationPassword = '';
    },
    closeSidebar() {
      this.resetState();
      this.$emit('closeSidebar', false);
    },
    nextStep() {
      if (this.currentStep < 3 && this.canAdvance) {
        this.currentStep += 1;
      }
    },
    prevStep() {
      if (this.currentStep > 1) {
        this.currentStep -= 1;
      }
    },
    confirmReplication() {
      if (!this.canConfirm) {
        return;
      }
      this.$emit('confirmReplication', {
        authorizationPassword: this.authorizationPassword
      });
    }
  }
}
</script>

<style lang="scss" scoped>
.chat-flow-confirm-popup ::v-deep .vs-popup {
  width: 760px;
  max-width: calc(100vw - 24px);
}

.wizard-content {
  padding: 8px 10px 12px;
}

.stepper {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-bottom: 20px;
}

.step-chip {
  border-radius: 999px;
  background: #f3f4f6;
  color: #4b5563;
  font-size: 12px;
  font-weight: 700;
  padding: 10px 12px;
  text-align: center;
}

.step-chip-active {
  background: #111827;
  color: #ffffff;
}

.step-chip-done {
  background: #dcfce7;
  color: #166534;
}

.step-panel {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.step-description {
  margin: 0;
  color: #374151;
  font-size: 13px;
  line-height: 1.5;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.summary-card,
.tenant-list-card,
.signature-box {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #f9fafb;
  padding: 12px;
}

.summary-card p,
.signature-box p {
  margin: 6px 0 0;
  color: #111827;
  font-size: 14px;
  font-weight: 600;
}

.summary-label {
  color: #6b7280;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.tenant-list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
  font-size: 12px;
  color: #111827;
}

.tenant-list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 240px;
  overflow-y: auto;
}

.tenant-list li {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #e5e7eb;
  font-size: 13px;
  color: #111827;
}

.tenant-list li:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.tenant-list small {
  color: #6b7280;
  text-align: right;
}

.risk-box {
  border: 1px solid #ef4444;
  background: #fef2f2;
  color: #991b1b;
  border-radius: 12px;
  padding: 14px;
  font-size: 13px;
  line-height: 1.6;
}

.risk-box p {
  margin: 0 0 8px;
}

.risk-box p:last-child {
  margin-bottom: 0;
}

.helper-text {
  color: #6b7280;
  font-size: 12px;
}

.wizard-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 24px;
}

@media (max-width: 720px) {
  .stepper,
  .summary-grid {
    grid-template-columns: 1fr;
  }

  .tenant-list li,
  .tenant-list-header,
  .wizard-actions {
    flex-direction: column;
    align-items: flex-start;
  }

  .wizard-actions {
    width: 100%;
  }
}

.background-info {
  margin-top: 12px;
  padding: 12px 14px;
  border-radius: 10px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1d4ed8;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.background-info strong {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.background-info p {
  margin: 0;
  font-size: 13px;
  line-height: 1.45;
  color: #1e3a8a;
}
</style>
